import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  generateSubconcienciaQuestion,
  postSubconcienciaResponse,
  regenerateSubconcienciaQuestion,
  getSubconcienciaNeuralDashboard
} from '../api';
import { AuthContext } from '../context/AuthContext';
import { useProgression } from '../context/ProgressionContext';
import { useAppBackground } from '../context/AppBackgroundContext';
import { useI18n } from '../i18n';
import ProfileBackground from '../components/ProfileBackground';
import ReadableText from '../components/ReadableText';
import { useAudio } from '../context/AudioContext';
import './Subconciencia.css';

const termMap = {
  'fear of not being good enough': 'Miedo a no ser suficiente',
  'exposure': 'Exposición',
  'vulnerability': 'Vulnerabilidad',
  'being judged': 'Miedo al juicio externo',
  'hesitation': 'Vacilación',
  'preparation': 'Preparación compulsiva',
  'avoidance': 'Evasión',
  'self-doubt': 'Duda de uno mismo',
  'self-acceptance': 'Autoaceptación',
  'prudence': 'Prudencia',
  'caution': 'Cautela',
  'authenticity': 'Autenticidad',
  'honesty': 'Honestidad',
  'secrets': 'Secretos',
  'family': 'Familia',
  'self-reflection': 'Autorreflexión',
  'personal growth': 'Crecimiento personal',
  'trust': 'Confianza',
  'intimacy': 'Intimidad',
  'connection': 'Conexión'
};

const translateTag = (tag) => {
  if (!tag || typeof tag !== 'string') return tag;
  const t = tag.toLowerCase().trim();
  return termMap[t] || tag;
};

const EMPTY_CHOICE = { shortText: '', text: '', symbol: '' };

const sanitizeAnomalyText = (anomaly) => {
  if (!anomaly) return { action: null, reason: '' };

  let action = null;
  let reason = '';

  // 1. Parse userResponse for action
  if (anomaly.userResponse) {
    try {
      const parsedResponse = typeof anomaly.userResponse === 'string'
        ? JSON.parse(anomaly.userResponse)
        : anomaly.userResponse;
      action = parsedResponse.label || String(anomaly.userResponse);
    } catch (e) {
      action = String(anomaly.userResponse);
    }
  }

  // 2. Parse contradictedContext for reason
  let aiReason = null;
  if (anomaly.contradictedContext) {
    try {
      const parsedContext = typeof anomaly.contradictedContext === 'string'
        ? JSON.parse(anomaly.contradictedContext)
        : anomaly.contradictedContext;
      aiReason = parsedContext.aiReason;
    } catch (e) {
      // Ignore parse error
    }
  }

  // 3. Fallback logic for reason
  if (aiReason) {
    reason = aiReason;
  } else {
    let text = '';
    if (typeof anomaly === 'object') {
      text = anomaly.contradiction || anomaly.message || anomaly.description || anomaly.details || anomaly.text || JSON.stringify(anomaly);
    } else {
      text = String(anomaly);
    }

    if (typeof text === 'string' && (text.trim().startsWith('{') || text.trim().startsWith('['))) {
      try {
        const parsed = JSON.parse(text);
        if (typeof parsed === 'object' && parsed !== null) {
          text = parsed.contradiction || parsed.reason || parsed.message || parsed.description || parsed.details || text;
        }
      } catch (e) {}
    }

    if (typeof text === 'string') {
      text = text.replace(/^["']|["']$/g, '');
      text = text.replace(/\\"/g, '"');
      text = text.replace(/^[{[\]}]|[{[\]}]$/g, '');
      text = text.trim();
    }
    reason = String(text);
  }

  return { action, reason };
};

const formatNeuralDate = (dateVal, onlyDate = false) => {
  if (!dateVal || (typeof dateVal === 'object' && Object.keys(dateVal).length === 0)) return '[ TIEMPO NO LINEAL // REGISTRO ATEMPORAL ]';
  const d = new Date(dateVal);
  if (isNaN(d.getTime())) return '[ TIEMPO NO LINEAL // REGISTRO ATEMPORAL ]';
  return onlyDate ? d.toISOString().split('T')[0] : d.toISOString().replace('T', ' ').substring(0, 19);
};

function buildFallbackDream(t) {
  return {
    question: t('subconsciousness.fallbackQuestion'),
    dreamPrompt: t('subconsciousness.fallbackPrompt'),
    left: {
      shortText: t('subconsciousness.fallbackLeftShort'),
      text: t('subconsciousness.fallbackLeftText'),
      symbol: '*'
    },
    right: {
      shortText: t('subconsciousness.fallbackRightShort'),
      text: t('subconsciousness.fallbackRightText'),
      symbol: '~'
    },
    tone: 'dream',
    topic: t('subconsciousness.fallbackTopic'),
    microInsight: t('subconsciousness.fallbackInsight'),
    fallback: true
  };
}

function normalizeChoice(choice = EMPTY_CHOICE, fallback = EMPTY_CHOICE) {
  const source = choice && typeof choice === 'object' ? choice : {};
  return {
    shortText: String(source.shortText || source.label || fallback.shortText || '').slice(0, 60),
    text: String(source.text || source.description || fallback.text || '').slice(0, 220),
    symbol: String(source.symbol || fallback.symbol || '').slice(0, 8)
  };
}

function normalizeDreamPayload(payload, t) {
  const fallback = buildFallbackDream(t);
  const source = payload && typeof payload === 'object' ? payload : {};
  return {
    question: String(source.question || fallback.question).slice(0, 180),
    dreamPrompt: String(source.dreamPrompt || fallback.dreamPrompt).slice(0, 220),
    left: normalizeChoice(source.left, fallback.left),
    right: normalizeChoice(source.right, fallback.right),
    tone: String(source.tone || fallback.tone).slice(0, 20),
    topic: String(source.topic || fallback.topic).slice(0, 100),
    microInsight: String(source.microInsight || fallback.microInsight).slice(0, 220),
    fallback: Boolean(source.fallback)
  };
}

function getChoiceReadText(choice = EMPTY_CHOICE) {
  return String(choice.shortText || choice.text || '').replace(/\s+/g, ' ').trim();
}

function buildCombinedOptionsText(dream, separator) {
  const left = getChoiceReadText(dream?.left);
  const right = getChoiceReadText(dream?.right);
  if (left && right) return `${left} ${separator} ${right}`;
  return left || right;
}

function SubconcienciaDreamLoader({ message, step }) {
  return (
    <section className="subconciencia-dream-loader" role="status" aria-live="polite">
      <div className="subconciencia-dream-tunnel" aria-hidden="true">
        <span className="dream-tunnel-ring ring-1" />
        <span className="dream-tunnel-ring ring-2" />
        <span className="dream-tunnel-ring ring-3" />
        <span className="dream-tunnel-ring ring-4" />
        <span className="dream-tunnel-mist mist-1" />
        <span className="dream-tunnel-mist mist-2" />
        <span className="dream-tunnel-thread thread-1" />
        <span className="dream-tunnel-thread thread-2" />
      </div>
      <p>{message}</p>
      {step && (
        <div className="dream-loading-sequence" aria-live="polite">
          <span className="dream-loading-sequence-text">{step}</span>
        </div>
      )}
    </section>
  );
}

const Subconciencia = () => {
  const { user } = useContext(AuthContext);
  const { displayedImage, settings } = useAppBackground() || {};
  const { t, language } = useI18n();
  const audio = useAudio();
  const { refreshState } = useProgression() || {};

  const [activeMode, setActiveMode] = useState('interactive'); // 'interactive' | 'dashboard'

  // ==========================================
  // INTERACTIVE MODE STATE
  // ==========================================
  const [dream, setDream] = useState(() => buildFallbackDream(t));
  const [selectedSide, setSelectedSide] = useState('');
  const [interactiveLoading, setInteractiveLoading] = useState(true);
  const [advancing, setAdvancing] = useState(false);
  const [interactiveError, setInteractiveError] = useState('');
  const [status, setStatus] = useState('');
  const [dreamLoadingStepIndex, setDreamLoadingStepIndex] = useState(0);

  const dreamLoadingSteps = useMemo(() => {
    const steps = t('subconsciousness.dreamLoadingSteps');
    return Array.isArray(steps) ? steps.filter(Boolean) : [];
  }, [t]);

  const dreamLoadingStep = dreamLoadingSteps[dreamLoadingStepIndex % Math.max(dreamLoadingSteps.length, 1)] || '';

  const selectedOption = useMemo(() => {
    if (selectedSide === 'left') return dream.left;
    if (selectedSide === 'right') return dream.right;
    return null;
  }, [dream, selectedSide]);

  const optionsReadText = useMemo(() => (
    buildCombinedOptionsText(dream, t('subconsciousness.optionsSeparator'))
  ), [dream, t]);

  const loadDream = useCallback(async ({ regenerate = false, previousSelection = '', avoidLastTopic = '' } = {}) => {
    if (!user?.id) return;
    setInteractiveLoading(true);
    setInteractiveError('');
    setStatus('');
    setSelectedSide('');
    window.dispatchEvent(new CustomEvent('q-stamina-pulse'));

    try {
      const payload = { avoidLastTopic, previousSelection };
      const response = regenerate
        ? await regenerateSubconcienciaQuestion(payload)
        : await generateSubconcienciaQuestion(payload);
      setDream(normalizeDreamPayload(response?.data, t));
    } catch (err) {
      console.error('Error obteniendo Subconciencia:', err);
      setDream(buildFallbackDream(t));
      setInteractiveError(t('subconsciousness.error'));
    } finally {
      setInteractiveLoading(false);
    }
  }, [t, user?.id]);

  useEffect(() => {
    if (activeMode === 'interactive') {
      loadDream();
    }
  }, [activeMode, loadDream]);

  useEffect(() => {
    if (!interactiveLoading || dreamLoadingSteps.length <= 1 || activeMode !== 'interactive') {
      setDreamLoadingStepIndex(0);
      return undefined;
    }
    const intervalId = window.setInterval(() => {
      setDreamLoadingStepIndex((currentIndex) => (currentIndex + 1) % dreamLoadingSteps.length);
    }, 2200);
    return () => window.clearInterval(intervalId);
  }, [dreamLoadingSteps.length, interactiveLoading, activeMode]);

  const handleSelect = (side) => {
    setSelectedSide(side);
    setStatus('');
  };

  const handleReadOptions = (event) => {
    event.preventDefault();
    event.stopPropagation();
    audio?.speak?.(optionsReadText, { label: t('subconsciousness.readOptions') });
  };

  const handleNext = async () => {
    if (!selectedOption) {
      setStatus(t('subconsciousness.chooseFirst'));
      return;
    }
    setAdvancing(true);
    setStatus(t('subconsciousness.sending'));

    try {
      await postSubconcienciaResponse({
        question: dream.question,
        dreamPrompt: dream.dreamPrompt,
        selectedSide,
        selectedOption,
        topic: dream.topic,
        tone: dream.tone,
        microInsight: dream.microInsight
      });
      if (refreshState) refreshState();
      setStatus(t('subconsciousness.saved'));
      await loadDream({
        regenerate: true,
        avoidLastTopic: dream.topic,
        previousSelection: selectedOption.text || selectedOption.shortText
      });
    } catch (err) {
      console.error('Error enviando Subconciencia:', err);
      setStatus(t('subconsciousness.responseError'));
    } finally {
      setAdvancing(false);
    }
  };


  // ==========================================
  // DASHBOARD MODE STATE
  // ==========================================
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');

  const [dashboardData, setDashboardData] = useState({
    anomalies: [],
    principles: [],
    traits: {},
    pagination: { currentPage: 1, totalPages: 1 },
    errors: {}
  });

  const loadDashboard = useCallback(async (page = 1) => {
    if (!user?.id) return;
    setDashboardLoading(true);
    setDashboardError('');

    try {
      const response = await getSubconcienciaNeuralDashboard(page, 20, language);
      if (response?.data) {
        setDashboardData(prev => ({
          ...prev,
          ...response.data
        }));
      }
    } catch (err) {
      console.error('Error loading Neural Dashboard:', err);
      setDashboardData(prev => ({
        ...prev,
        errors: { global: true }
      }));
    } finally {
      setDashboardLoading(false);
    }
  }, [user?.id, language]);

  useEffect(() => {
    if (activeMode === 'dashboard') {
      loadDashboard();
    }
  }, [activeMode, loadDashboard]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= dashboardData.pagination.totalPages) {
      loadDashboard(newPage);
    }
  };


  if (!user?.id) {
    return <p className="neural-login-required">ACCESS DENIED. LOGIN REQUIRED.</p>;
  }

  // Derived dashboard variables
  const { anomalies, principles, traits, pagination, errors } = dashboardData;

  // Check if traits is effectively empty (all primary arrays missing or empty)
  const isTraitsEmpty = !traits || (
    (!traits.fears || traits.fears.length === 0) &&
    (!traits.habits || traits.habits.length === 0) &&
    (!traits.values || traits.values.length === 0) &&
    (!traits.interests || traits.interests.length === 0) &&
    (!traits.relationships || traits.relationships.length === 0)
  );

  return (
    <>
      {settings?.enabled && displayedImage && <ProfileBackground imageData={displayedImage} interactive={false} />}

      {activeMode === 'dashboard' ? (
        // ==========================================
        // DASHBOARD RENDER
        // ==========================================
        <main id="neural-dashboard-main" className="neural-dashboard-page" style={{ position: 'relative', zIndex: 2 }}>
          {errors?.global && (
            <div className="neural-global-warn blink">
              [SYS_WARN: API CONNECTION SEVERED. SHOWING CACHED/EMPTY DATA]
            </div>
          )}

          <div className="neural-mode-selector">
            <button
              className={`neural-tab-btn ${activeMode === 'interactive' ? 'active' : ''}`}
              onClick={() => setActiveMode('interactive')}
            >
              [ ⊙ INTERACCIÓN ONÍRICA ]
            </button>
            <button
              className={`neural-tab-btn ${activeMode === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveMode('dashboard')}
            >
              [ ⚡ TERMINAL NEURAL (ROOT ACCESS) ]
            </button>
          </div>

          <header className="neural-header">
            <p className="neural-kicker">TERMINAL DE EXTRACCIÓN NEURAL</p>
            <h1>LIBRO ROJO // ROOT ACCESS</h1>
            <p className="neural-prompt">
              &gt; Inicializando escaneo de memoria... <br/>
              &gt; Desplegando vectores conductuales...
            </p>
          </header>

          {dashboardLoading ? (
            <div className="neural-loader">
              <span className="blink">_EXTRAYENDO_DATOS_</span>
            </div>
          ) : dashboardError ? (
            <div className="neural-error">{dashboardError}</div>
          ) : (
            <section className="neural-grid">
              {/* Panel A: Vectores Nucleares */}
              <div className="neural-panel panel-a">
                <h2>[ DATA CORE / VECTORES NUCLEARES ]</h2>
                <div className="neural-panel-content">
                  {errors?.principles && <p className="neural-error-inline">[CORE_WARN: VECTOR_OFFLINE]</p>}
                  {!(principles?.length > 0) ? (
                    <p className="neural-empty">[ AWAITING REM CYCLE CONSOLIDATION - 0 VECTORS INDEXED ]</p>
                  ) : (
                    <ul className="neural-list">
                      {principles.map(p => {
                        let embeddingStr = '';
                        if (p.embedding) {
                           let arr = [];
                           try {
                             arr = typeof p.embedding === 'string' ? JSON.parse(p.embedding) : p.embedding;
                           } catch(e) {
                             // Ignore parse error
                           }
                           if (Array.isArray(arr) && arr.length > 0) {
                             embeddingStr = `[${arr.slice(0, 3).map(v => Number(v).toFixed(4)).join(', ')}, ...]`;
                           }
                        }

                        return (
                          <li key={p.id} className="neural-list-item">
                            <span className="neural-hash">0x{Math.random().toString(16).substring(2, 8).toUpperCase()}</span>
                            <div className="neural-text">
                              {p.principle || p.content}
                              {embeddingStr && <div className="neural-embedding" style={{fontSize: '0.8em', color: '#888', marginTop: '4px'}}>{embeddingStr}</div>}
                            </div>
                            <span className="neural-date">IDX: {formatNeuralDate(p.createdAt || p.created_at, true)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              {/* Panel B: Espejo Conductual */}
              <div className="neural-panel panel-b">
                <h2>[ ESPEJO CONDUCTUAL ]</h2>
                <div className="neural-panel-content">
                  {errors?.traits && <p className="neural-error-inline">[CORE_WARN: TRAITS_OFFLINE]</p>}
                  {isTraitsEmpty ? (
                    <div className="neural-traits-empty">
                      <p className="neural-empty">[ ESTADO: PERFIL EN PROCESO DE CRISTALIZACIÓN ]</p>
                      <p className="neural-subtitle">El Módulo REM requiere más interacciones vectoriales para sintetizar tus patrones conductuales.</p>
                    </div>
                  ) : (
                    <div lang="es" className="neural-traits-wrapper">
                      {traits.last_tone && (
                        <div className="neural-trait-category">
                          <h3>🎭 TONO CONDUCTUAL</h3>
                          <div className="neural-badges">
                            <span className="neural-badge tone-badge">[TONO: {translateTag(traits.last_tone).toUpperCase()}]</span>
                          </div>
                        </div>
                      )}
                      {traits.values && traits.values.length > 0 && (
                        <div className="neural-trait-category">
                          <h3>🎯 VALORES / VALUES</h3>
                          <div className="neural-badges">
                            {traits.values.map((v, i) => <span key={i} className="neural-badge">{translateTag(v)}</span>)}
                          </div>
                        </div>
                      )}
                      {traits.habits && traits.habits.length > 0 && (
                        <div className="neural-trait-category">
                          <h3>⚡ HÁBITOS / HABITS</h3>
                          <div className="neural-badges">
                            {traits.habits.map((h, i) => <span key={i} className="neural-badge">{translateTag(h)}</span>)}
                          </div>
                        </div>
                      )}
                      {traits.fears && traits.fears.length > 0 && (
                        <div className="neural-trait-category">
                          <h3>⚠️ TEMORES / FEARS</h3>
                          <div className="neural-badges">
                            {traits.fears.map((f, i) => <span key={i} className="neural-badge">{translateTag(f)}</span>)}
                          </div>
                        </div>
                      )}
                      {traits.interests && traits.interests.length > 0 && (
                        <div className="neural-trait-category">
                          <h3>🔬 INTERESES / INTERESTS</h3>
                          <div className="neural-badges">
                            {traits.interests.map((int, i) => <span key={i} className="neural-badge">{translateTag(int)}</span>)}
                          </div>
                        </div>
                      )}
                      {traits.relationships && traits.relationships.length > 0 && (
                        <div className="neural-trait-category">
                          <h3>🤝 RELACIONES / RELATIONSHIPS</h3>
                          <div className="neural-badges">
                            {traits.relationships.map((r, i) => <span key={i} className="neural-badge">{translateTag(r)}</span>)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Panel C: Log de Anomalías (Inquisición) */}
              <div className="neural-panel panel-c">
                <h2>[ LOG DE ANOMALÍAS / INQUISICIÓN ]</h2>
                <div className="neural-panel-content">
                  {errors?.anomalies && <p className="neural-error-inline">[CORE_WARN: ANOMALIES_OFFLINE]</p>}
                  {!(anomalies?.length > 0) ? (
                    <p className="neural-empty" style={{ fontFamily: 'monospace' }}>[ STATUS: ALINEACIÓN PERFECTA ] - 0 contradicciones cognitivas registradas en el Módulo de Inquisición.</p>
                  ) : (
                    <div className="neural-anomalies-list">
                      {anomalies.map(a => {
                        const sanitized = sanitizeAnomalyText(a);
                        return (
                          <div key={a.id} className="neural-anomaly-card">
                            <div className="neural-anomaly-header">
                              <span className="neural-anomaly-title">[ INQUISICIÓN // DISONANCIA DETECTADA ]</span>
                              <span className="neural-anomaly-date">{formatNeuralDate(a.createdAt || a.timestamp || a.date || a.created_at, false)}</span>
                            </div>
                            <div className="anomaly-body" style={{ fontFamily: 'monospace' }}>
                              {sanitized.action && <p><strong>ACCIÓN:</strong> {sanitized.action}</p>}
                              <p><strong>DISONANCIA:</strong> {sanitized.reason}</p>
                            </div>
                            <div className="neural-anomaly-footer">
                              <span className="neural-badge">[ ESTADO: REGISTRADO EN MATRIZ ]</span>
                              <span className="neural-badge">[{a.type || a.category || a.level || 'DIVERGENCIA'}]</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {pagination.totalPages > 1 && (
                    <div className="neural-pagination">
                      <button
                        onClick={() => handlePageChange(pagination.currentPage - 1)}
                        disabled={pagination.currentPage === 1}
                      >
                        &lt; PREV
                      </button>
                      <span>{pagination.currentPage} / {pagination.totalPages}</span>
                      <button
                        onClick={() => handlePageChange(pagination.currentPage + 1)}
                        disabled={pagination.currentPage === pagination.totalPages}
                      >
                        NEXT &gt;
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>
          )}
        </main>
      ) : (
        // ==========================================
        // INTERACTIVE RENDER
        // ==========================================
        <main id="subconsciousness-main" className={'subconciencia-page tone-' + (dream.tone || 'dream')} style={{ position: 'relative', zIndex: 2 }}>

          <div className="neural-mode-selector interactive-mode-selector">
            <button
              className={`neural-tab-btn ${activeMode === 'interactive' ? 'active' : ''}`}
              onClick={() => setActiveMode('interactive')}
            >
              [ ⊙ INTERACCIÓN ONÍRICA ]
            </button>
            <button
              className={`neural-tab-btn ${activeMode === 'dashboard' ? 'active' : ''}`}
              onClick={() => setActiveMode('dashboard')}
            >
              [ ⚡ TERMINAL NEURAL (ROOT ACCESS) ]
            </button>
          </div>

          <header className="subconciencia-header">
            <p className="subconciencia-kicker">{t('subconsciousness.kicker')}</p>
            <h1>{t('subconsciousness.title')}</h1>
            <p className="subconciencia-dream-prompt">
              <ReadableText text={dream.dreamPrompt} label={t('subconsciousness.readPrompt')}>
                {dream.dreamPrompt}
              </ReadableText>
            </p>
          </header>

          {interactiveLoading ? (
            <SubconcienciaDreamLoader
              message={t('subconsciousness.dreamTunnelLoading')}
              step={dreamLoadingStep}
            />
          ) : (
            <section className="subconciencia-stage" aria-label={t('subconsciousness.stageAria')}>
              <div className="subconciencia-question-panel">
                <h2>
                  <ReadableText text={dream.question} label={t('subconsciousness.readQuestion')}>
                    {dream.question}
                  </ReadableText>
                </h2>
                {dream.microInsight && (
                  <p className="subconciencia-insight">
                    <ReadableText text={dream.microInsight} label={t('subconsciousness.readInsight')}>
                      {dream.microInsight}
                    </ReadableText>
                  </p>
                )}
              </div>

              <div className="subconciencia-options-toolbar">
                <button
                  type="button"
                  className="subconciencia-read-options"
                  onClick={handleReadOptions}
                  aria-label={t('subconsciousness.readOptionsAria')}
                >
                  {t('subconsciousness.readOptions')}
                </button>
              </div>

              <div className="subconciencia-binary" role="group" aria-label={t('subconsciousness.optionsAria')}>
                {['left', 'right'].map((side) => {
                  const option = dream[side] || EMPTY_CHOICE;
                  const isSelected = selectedSide === side;
                  return (
                    <button
                      key={side}
                      type="button"
                      className={'subconciencia-choice ' + side + (isSelected ? ' selected' : '')}
                      onClick={() => handleSelect(side)}
                      aria-pressed={isSelected}
                      aria-label={t(side === 'left' ? 'subconsciousness.leftAria' : 'subconsciousness.rightAria', { text: option.text || option.shortText })}
                    >
                      <span className="subconciencia-choice-symbol" aria-hidden>{option.symbol || (side === 'left' ? '*' : '~')}</span>
                      <span className="subconciencia-choice-short">{option.shortText}</span>
                      <span className="subconciencia-choice-text">{option.text}</span>
                    </button>
                  );
                })}
              </div>

              {interactiveError && <p className="subconciencia-error">{interactiveError}</p>}
              {status && <p className="subconciencia-status">{status}</p>}

              <div className="subconciencia-actions">
                <button type="button" className="subconciencia-next" onClick={handleNext} disabled={advancing}>
                  {advancing ? t('subconsciousness.advancing') : t('subconsciousness.next')}
                </button>
                <button type="button" className="subconciencia-retry" onClick={() => loadDream({ regenerate: true, avoidLastTopic: dream.topic })} disabled={advancing}>
                  {t('subconsciousness.retry')}
                </button>
              </div>
            </section>
          )}
        </main>
      )}
    </>
  );
};

export default Subconciencia;
